import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/DateFloatingPanel'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'
import { formatDate } from 'components/SearchV2/Utils/GlobalSearchV2'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import type { ListingDrawerHotelDateSelection } from './ListingDrawerSearchDates'

interface Props {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  onConfirm: (selectionOverride?: ListingDrawerHotelDateSelection) => void
  anytimePlaceholder: string
}

function ListingDrawerMobileHotelDateFields({
  isOpen,
  onOpen,
  onClose,
  onConfirm,
  anytimePlaceholder,
}: Props) {
  const intl = useIntl()
  const {
    checkinDate,
    checkoutDate,
    dateSearchOptionId,
    flexibleMonths,
    flexibleNights,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const handleAnytimeDatesSelected = useCallback(() => {
    onConfirm({ type: 'anytime' })
  }, [onConfirm])

  const dateSearchProps = useDateSearchProps({
    onAnytimeDatesSelected: handleAnytimeDatesSelected,
  })

  const applyButtonLabel = intl.formatMessage({
    id: 'ENxPPE_4gjNvtXZpYAUq',
    defaultMessage: 'Apply',
    description: 'Apply button text for search form panels in the search interface',
  })

  const whenLabel = intl.formatMessage({
    id: 'G-va_y_Ri6pCw2Ek4uPR',
    defaultMessage: 'When',
    description: 'Label for the date search field input in the main search and discovery interface',
  })

  const checkInLabel = intl.formatMessage({
    id: 'BZImHfWDmEabHe95A4Ie',
    defaultMessage: 'Check in',
    description: 'Form field label for the hotel check-in date in the booking date picker',
  })

  const checkOutLabel = intl.formatMessage({
    id: 'zNj1m3NcaWN8Inz8iuy9',
    defaultMessage: 'Check out',
    description: 'Label for check-out date field in the date selection or calendar picker',
  })

  const flexibleSearchSelectionText = useMemo(
    () => prettyFlexibleDates(
      userSelectedFlexibleMonths ? flexibleMonths : undefined,
      flexibleNights,
      undefined,
      intl,
    ),
    [flexibleMonths, flexibleNights, intl, userSelectedFlexibleMonths],
  )

  const checkInDisplayValue = useMemo(() => formatDate(checkinDate), [checkinDate])
  const checkOutDisplayValue = useMemo(() => formatDate(checkoutDate), [checkoutDate])

  return (
    <>
      <DateFloatingPanel
        {...dateSearchProps}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={onConfirm}
        onGoBack={onClose}
        confirmText={applyButtonLabel}
      />

      {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
        <SearchFormField
          label={whenLabel}
          onClick={onOpen}
          placeholder={anytimePlaceholder}
          value={flexibleSearchSelectionText}
        />
      )}

      {dateSearchOptionId !== DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
        <SearchFormFieldGroup>
          <SearchFormField
            label={checkInLabel}
            placeholder={anytimePlaceholder}
            value={checkInDisplayValue}
            onClick={onOpen}
          />
          <SearchFormField
            label={checkOutLabel}
            placeholder={anytimePlaceholder}
            value={checkOutDisplayValue}
            onClick={onOpen}
          />
        </SearchFormFieldGroup>
      )}
    </>
  )
}

export default ListingDrawerMobileHotelDateFields
